import { FC, ReactNode } from 'react';

import { useRecoilValue } from 'recoil';

import GiftStoreAuthGate from '@service/giftStore/components/GiftStoreAuthGate';
import { GiftStoreNavBar } from '@service/giftStore/containers/GiftStoreNavBar/GiftStoreNavBar';
import { giftStoreNavBarTransparentAtom } from '@service/giftStore/containers/GiftStoreNavBar/giftStoreNavBar.atom';
import { GiftErrorFallback } from '@service/giftStore/components/GiftErrorFallback';
import GiftCartPage from '@service/giftStore/page/GiftCartPage';
import GiftCheckoutPage from '@service/giftStore/page/GiftCheckoutPage';
import GiftCouponRedeemPage from '@service/giftStore/page/GiftCouponRedeemPage';
import GiftCouponRefundPage from '@service/giftStore/page/GiftCouponRefundPage';
import GiftCouponRenewalPage from '@service/giftStore/page/GiftCouponRenewalPage';
import GiftDeliveryAddressSubmitPage from '@service/giftStore/page/GiftDeliveryAddressSubmitPage';
import GiftOrderCouponPage from '@service/giftStore/page/GiftOrderCouponPage';
import GiftOrderDeliveryStatusPage from '@service/giftStore/page/GiftOrderDeliveryStatusPage';
import GiftOrderListPage from '@service/giftStore/page/GiftOrderListPage';
import GiftOrderPaymentResultPage from '@service/giftStore/page/GiftOrderPaymentResultPage';
import GiftStoreMainPage from '@service/giftStore/page/GiftStoreMainPage';
import GiftStoreTermsPage from '@service/giftStore/page/GiftStoreTermsPage';
import { useRedirect } from '@shared/hooks/useRedirect';
import { ErrorBoundary } from 'react-error-boundary';
import { Route, Routes, useLocation } from 'react-router-dom';

import GiftItemIdRouter from './GiftItemIdRouter';

const RedirectGate: FC<{ children: ReactNode }> = ({ children }) => {
  const isDirectAccess =
    !document.referrer || !document.referrer.startsWith(window.location.origin);
  useRedirect({ skip: !isDirectAccess });
  return <>{children}</>;
};

export const GiftStoreRouter: FC = function GiftStoreRouter() {
  const isTransparent = useRecoilValue(giftStoreNavBarTransparentAtom);
  const { pathname } = useLocation();

  return (
    <ErrorBoundary FallbackComponent={GiftErrorFallback} resetKeys={[pathname]}>
      <Routes>
      {/* NavBar 없이 렌더링되는 페이지 */}
      <Route path="coupon/:couponCode" element={<GiftCouponRedeemPage />} />
      <Route path="coupon/:couponId/refund" element={<GiftCouponRefundPage />} />
      <Route path="coupon/:couponId/renewal" element={<GiftCouponRenewalPage />} />
      <Route path="delivery/:deliveryCode" element={<GiftDeliveryAddressSubmitPage />} />

      {/* NavBar와 함께 렌더링되는 페이지들 */}
      <Route
        path="*"
        element={
          <div className="max-w-inherit">
            <GiftStoreNavBar />
            <div className={isTransparent ? '' : 'pt-14'}>
              <Routes>
                <Route path="" element={<GiftStoreMainPage />} />
                <Route path="cart" element={<GiftCartPage />} />
                <Route path="checkout" element={<GiftCheckoutPage />} />
                <Route
                  path="orders"
                  element={
                    <GiftStoreAuthGate>
                      <GiftOrderListPage />
                    </GiftStoreAuthGate>
                  }
                />
                <Route path="terms" element={<GiftStoreTermsPage />} />
                <Route
                  path="order/:orderId/payment"
                  element={<GiftOrderPaymentResultPage />}
                />
                <Route
                  path="order/:orderId/coupon"
                  element={
                    <RedirectGate>
                      <GiftStoreAuthGate>
                        <GiftOrderCouponPage />
                      </GiftStoreAuthGate>
                    </RedirectGate>
                  }
                />
                <Route
                  path="order/:orderId/delivery"
                  element={
                    <RedirectGate>
                      <GiftStoreAuthGate>
                        <GiftOrderCouponPage />
                      </GiftStoreAuthGate>
                    </RedirectGate>
                  }
                />
                <Route
                  path="order/:orderId/delivery-status"
                  element={
                    <RedirectGate>
                      <GiftStoreAuthGate>
                        <GiftOrderDeliveryStatusPage />
                      </GiftStoreAuthGate>
                    </RedirectGate>
                  }
                />
                <Route path="item/:itemId/*" element={<GiftItemIdRouter />} />
              </Routes>
            </div>
          </div>
        }
      />
      </Routes>
    </ErrorBoundary>
  );
};
